import type { TimelineItem } from "../../domain/timeline";

/** http(s) URL for a stored website; adds https:// when the scheme is missing. */
export const safeWebsiteUrl = (
  raw: string | null | undefined,
): string | null => {
  const value = raw?.trim();
  if (!value) return null;
  const isHttp = /^https?:\/\//i.test(value);
  // Any other scheme (javascript:, ftp:, mailto:…) is refused; "host:port" is not a scheme.
  if (!isHttp && /^[a-z][a-z\d+.-]*:(?!\d)/i.test(value)) return null;
  const candidate = isHttp ? value : `https://${value}`;
  try {
    return new URL(candidate).hostname ? candidate : null;
  } catch {
    return null;
  }
};

const byAtThenIdDesc = (a: TimelineItem, b: TimelineItem): number =>
  a.at === b.at ? b.id.localeCompare(a.id) : b.at.localeCompare(a.at);

/**
 * Flattens loaded pages, keeps the first copy of each id (rows can shift across
 * page boundaries while new activity arrives), and orders by (at DESC, id DESC).
 */
export const mergeTimelinePages = (
  pages: readonly { data: readonly TimelineItem[] }[],
): TimelineItem[] => {
  const seen = new Set<string>();
  const items: TimelineItem[] = [];
  for (const page of pages)
    for (const item of page.data) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  return items.sort(byAtThenIdDesc);
};
